import React, { useState } from 'react';

type Platform = 'windows' | 'mac' | 'android' | 'apple';

interface PlatformConfig {
  label: string;
  faIcon: string;
  iconColor: string;
  available: boolean;
  downloadUrl?: string;
  fileName?: string;
  description: string;
  comingSoon?: string;
}

const PLATFORMS: Record<Platform, PlatformConfig> = {
  windows: {
    label: 'Windows',
    faIcon: 'fa-brands fa-windows',
    iconColor: '#0078D4',
    available: true,
    downloadUrl: 'https://dn9souybgk5tq.cloudfront.net/downloads/DataCrateSync-Setup.exe',
    fileName: 'DataCrateSync-Setup.exe',
    description: 'Data Crate Sync for Windows',
  },
  mac: {
    label: 'macOS',
    faIcon: 'fa-brands fa-apple',
    iconColor: '#555555',
    available: false,
    description: 'Data Crate Sync for macOS',
    comingSoon: 'macOS installer coming soon'
  },
  android: {
    label: 'Android',
    faIcon: 'fa-brands fa-android',
    iconColor: '#3DDC84',
    available: false,
    description: 'Data Crate for Android',
    comingSoon: 'Android app coming soon'
  },
  apple: {
    label: 'iOS / iPadOS',
    faIcon: 'fa-brands fa-app-store-ios',
    iconColor: '#0D96F6',
    available: false,
    description: 'Data Crate for iPhone & iPad',
    comingSoon: 'iOS app coming soon'
  }
};

const PLATFORM_ORDER: Platform[] = ['windows', 'mac', 'android', 'apple'];

const DownloadPage: React.FC = () => {
  const [selected, setSelected] = useState<Platform>('windows');
  const platform = PLATFORMS[selected];

  return (
    <div style={{
      display: 'flex',
      gap: '2rem',
      padding: '2rem',
      height: 'calc(100vh - 80px)',
      width: '100%'
    }}>
      {/* Left sidebar — platform tabs */}
      <div style={{
        flex: '0 0 250px',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <h2 style={{ marginBottom: '1rem' }}>Download</h2>
        {PLATFORM_ORDER.map(p => (
          <button
            key={p}
            onClick={() => setSelected(p)}
            style={{
              padding: '0.75rem 1.5rem',
              background: selected === p ? '#e6ac00' : '#ffc107',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              textAlign: 'left',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontWeight: selected === p ? 600 : 400
            }}
          >
            <i
              className={PLATFORMS[p].faIcon}
              style={{ fontSize: '1.1rem', color: selected === p ? '#000' : PLATFORMS[p].iconColor, width: 20, textAlign: 'center' }}
            />
            {PLATFORMS[p].label}
          </button>
        ))}
      </div>

      {/* Right panel — download details */}
      <div style={{
        flex: 1,
        minWidth: 0,
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem'
      }}>
        {/* OS logo — large Font Awesome icon */}
        <i
          className={platform.faIcon}
          style={{ fontSize: '7rem', color: platform.iconColor, lineHeight: 1 }}
        />

        {/* Title */}
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            {platform.description}
          </h3>
          <p style={{ color: '#666', fontSize: '0.9rem', maxWidth: 420 }}>
            {platform.available
              ? 'Download and install the Data Crate Sync desktop application to automatically sync your folders to your crates.'
              : platform.comingSoon
            }
          </p>
        </div>

        {/* Download button or coming soon badge */}
        {platform.available ? (
          <a
            href={platform.downloadUrl}
            download={platform.fileName}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.6rem',
              padding: '0.85rem 2rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '10px',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(102,126,234,0.4)'
            }}
          >
            <i className="fa-solid fa-download" />
            Download for {platform.label}
          </a>
        ) : (
          <div style={{
            padding: '0.75rem 2rem',
            background: '#f3f4f6',
            color: '#6b7280',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: 500,
            border: '1px dashed #d1d5db'
          }}>
            🚧 Coming Soon
          </div>
        )}

        {platform.available && (
          <>
            <p style={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center' }}>
              Version 1.2.0 · Windows x64 · ~114 MB
            </p>
            <div style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '10px',
              padding: '1rem 1.25rem',
              maxWidth: 460,
              fontSize: '0.85rem',
              color: '#92400e',
              lineHeight: 1.6
            }}>
              <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>⚠ Windows Security Notice</div>
              Windows Defender SmartScreen may flag this app as unrecognised because it is not yet digitally signed.
              If you choose to proceed, click <strong>"More info"</strong> then <strong>"Run anyway"</strong>.
              Alternatively, right-click the downloaded file → <strong>Properties</strong> → tick <strong>"Unblock"</strong> → OK.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DownloadPage;
